/**
 * Training Polling Hook
 * Centralizes all polling logic for training workflow
 */

import { useCallback } from 'react';
import { API_CONFIG, POLLING_CONFIG } from '../config/constants';
import { AnalysisStatus, TrainingStatus, WorkflowStep } from '../types';
import type { TFunction } from 'i18next';
import { trainingStatusApi } from '@/api';
import { getAuthToken } from '../../../core/auth/supabase-auth';
import { normalizeTrainingStatus } from '../utils/normalizeTrainingStatus';

export interface PollingCallbacks {
    setAnalysisStatus: (status: AnalysisStatus) => void;
    updateAnalysisProgress: (progress: number, step: string) => void;
    setErrorMessage: (message: string | null) => void;
    setSuccessMessage: (message: string | null) => void;
    setTrainingStatus: (status: TrainingStatus) => void;
    completeModelTraining: () => void;
    setIsGeneratingDatasets: (value: boolean) => void;
    setWorkflowStep: (step: WorkflowStep) => void;
    setIsTrainingModels: (value: boolean) => void;
    loadViolinPlotsFromResults: (sessionId: string) => Promise<void>;
}

export const useTrainingPolling = (
    uploadSessionId: string | null,
    analysisStatus: AnalysisStatus,
    mountedRef: React.MutableRefObject<boolean>,
    callbacks: PollingCallbacks,
    t: TFunction
) => {
    const {
        setAnalysisStatus,
        updateAnalysisProgress,
        setErrorMessage,
        setSuccessMessage,
        setTrainingStatus,
        completeModelTraining,
        setIsGeneratingDatasets,
        setWorkflowStep,
        setIsTrainingModels,
        loadViolinPlotsFromResults
    } = callbacks;

    /**
     * Poll analysis status during data processing
     */
    const pollAnalysisStatus = useCallback(async (): Promise<boolean> => {
        if (!uploadSessionId || !mountedRef.current) return true;

        try {
            const token = await getAuthToken();
            if (!token) {
                console.error('[pollAnalysisStatus] No auth token available');
                return false;
            }

            const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.TRAINING_STATUS}/${uploadSessionId}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok || !mountedRef.current) {
                return false;
            }

            const data = await response.json();

            if (normalizeTrainingStatus(data.status) === 'not_found' && analysisStatus === 'running') {
                try {
                    // Use lightweight /results-summary endpoint instead of full /results
                    const resultsResponse = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.TRAINING_RESULTS_SUMMARY}/${uploadSessionId}`, {
                        method: 'GET',
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json',
                        },
                    });
                    if (resultsResponse.ok && mountedRef.current) {
                        const summaryData = await resultsResponse.json();
                        // Check if training results exist
                        if (summaryData.has_training_results || summaryData.has_trained_model) {
                            setAnalysisStatus('completed');
                            updateAnalysisProgress(100, 'Training completed');
                            return true;
                        }
                    }
                } catch {
                    // Ignore results fetch errors
                }
                updateAnalysisProgress(0, 'Training läuft... (Backend status wird aktualisiert)');
                return false;
            }

            if (!mountedRef.current) return true;

            updateAnalysisProgress(data.progress || 0, data.current_step || data.message || '');

            const state = normalizeTrainingStatus(data.status);
            if (state === 'completed') {
                setAnalysisStatus('completed');
                return true;
            } else if (state === 'failed') {
                setAnalysisStatus('failed');
                setErrorMessage(t('training.notifications.errors.analysisFailed', { message: data.message || t('training.notifications.errors.unknown') }));
                return true;
            } else if (state === 'running') {
                setAnalysisStatus('running');
                return false;
            }
            return false;
        } catch {
            // Polling error handled silently
            return false;
        }
    }, [uploadSessionId, analysisStatus, setAnalysisStatus, updateAnalysisProgress, t, mountedRef]);

    /**
     * Poll training status during model training
     */
    const pollTrainingStatus = useCallback(async (): Promise<boolean> => {
        if (!uploadSessionId || !mountedRef.current) return true;

        try {
            const data = await trainingStatusApi.pollTrainingStatus(uploadSessionId);

            if (!mountedRef.current) return true;

            const state = normalizeTrainingStatus(data.status);
            if (state === 'completed') {
                setTrainingStatus('completed');
                completeModelTraining();
                setSuccessMessage(t('training.notifications.success.trainingCompleted'));
                return true;
            } else if (state === 'running') {
                setTrainingStatus('running');
                return false;
            } else if (state === 'failed') {
                setTrainingStatus('failed');
                setErrorMessage(t('training.notifications.errors.trainingFailed'));
                return true;
            }
            return false;
        } catch {
            return false;
        }
    }, [uploadSessionId, setTrainingStatus, completeModelTraining, setSuccessMessage, setErrorMessage, t, mountedRef]);

    /**
     * Poll dataset generation status
     */
    const pollDatasetGeneration = useCallback(async (sessionId: string) => {
        let pollAttempts = 0;
        const maxPollAttempts = POLLING_CONFIG.MAX_POLLING_ATTEMPTS;

        const pollInterval = setInterval(async () => {
            pollAttempts++;

            if (pollAttempts > maxPollAttempts || !mountedRef.current) {
                if (mountedRef.current) {
                    setIsGeneratingDatasets(false);
                    setErrorMessage(t('training.notifications.errors.datasetGenerationTimeout'));
                }
                clearInterval(pollInterval);
                return;
            }

            try {
                const token = await getAuthToken();
                if (!token) {
                    console.error('[pollDatasetGeneration] No auth token available');
                    clearInterval(pollInterval);
                    return;
                }

                const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.TRAINING_STATUS}/${sessionId}`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (!mountedRef.current) {
                    clearInterval(pollInterval);
                    return;
                }

                if (response.ok) {
                    const data = await response.json();

                    const state = normalizeTrainingStatus(data.status);
                    if (state === 'failed') {
                        setIsGeneratingDatasets(false);
                        updateAnalysisProgress(0, '');
                        const errorMessage = data.error_message || data.message || t('training.notifications.errors.trainingFailed');
                        setErrorMessage(errorMessage);
                        clearInterval(pollInterval);
                        return;
                    }

                    if (data.progress >= 100 || state === 'completed') {
                        setIsGeneratingDatasets(false);
                        setWorkflowStep('phase2');
                        updateAnalysisProgress(100, 'Training completed successfully');
                        await loadViolinPlotsFromResults(sessionId);
                        clearInterval(pollInterval);
                    } else if (state === 'running') {
                        updateAnalysisProgress(data.progress || 0, data.current_step || 'Processing...');
                    }
                }
            } catch {
                // Polling error handled silently
            }
        }, POLLING_CONFIG.DATASET_GENERATION_INTERVAL);
    }, [setIsGeneratingDatasets, setWorkflowStep, updateAnalysisProgress, setErrorMessage, loadViolinPlotsFromResults, t, mountedRef]);

    /**
     * Poll model training status
     */
    const pollModelTraining = useCallback(async (sessionId: string) => {
        const pollInterval = setInterval(async () => {
            if (!mountedRef.current) {
                clearInterval(pollInterval);
                return;
            }

            try {
                const token = await getAuthToken();
                if (!token) {
                    console.error('[pollModelTraining] No auth token available');
                    clearInterval(pollInterval);
                    return;
                }

                const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.TRAINING_STATUS}/${sessionId}`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (!mountedRef.current) {
                    clearInterval(pollInterval);
                    return;
                }

                if (response.ok) {
                    const data = await response.json();

                    if (data.success && data.data) {
                        const status = data.data.status;

                        if (status === 'training_completed') {
                            setIsTrainingModels(false);
                            completeModelTraining();
                            updateAnalysisProgress(100, t('training.notifications.success.modelTrainingCompleted'));
                            clearInterval(pollInterval);
                        } else if (status === 'failed') {
                            setIsTrainingModels(false);
                            setErrorMessage(t('training.notifications.errors.trainingFailed'));
                            clearInterval(pollInterval);
                        }
                    }
                }
            } catch {
                // Polling error handled silently
            }
        }, POLLING_CONFIG.MODEL_TRAINING_INTERVAL);
    }, [setIsTrainingModels, completeModelTraining, updateAnalysisProgress, setErrorMessage, t, mountedRef]);

    return {
        pollAnalysisStatus,
        pollTrainingStatus,
        pollDatasetGeneration,
        pollModelTraining
    };
};
